import { Injectable, inject } from '@angular/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { ChunkLoadErrorModalComponent } from 'src/app/components/chunk-load-error-modal/chunk-load-error-modal.component';
import { OpenModal } from 'src/app/states/actions/modal.actions';
import { isChunkLoadError } from 'src/app/utils/is-chunk-load-error.util';

/**
 * Listens for Angular Router navigation failures caused by a lazy route chunk that no longer
 * exists on the server (stale chunk after a deployment). Retries the failed navigation once to
 * rule out a transient network blip; if the retry also fails, prompts the user to refresh via
 * `ChunkLoadErrorModalComponent` instead of leaving them on a broken screen.
 */
@Injectable({
  providedIn: 'root'
})
export class ChunkLoadErrorHandlerService {
  private readonly router = inject(Router);
  private readonly store = inject(Store);

  // Tracks URLs that have already had one silent retry, keyed by URL rather than a single shared
  // flag so that concurrent/interleaved navigation failures for different routes (e.g. two lazy
  // route clicks in quick succession) don't corrupt each other's retry state.
  private readonly retriedUrls = new Set<string>();

  constructor() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationError) {
        this.handleNavigationError(event);
      } else if (event instanceof NavigationEnd) {
        this.retriedUrls.delete(event.url);
      }
    });
  }

  private handleNavigationError(event: NavigationError): void {
    if (!isChunkLoadError(event.error)) {
      return;
    }

    if (!this.retriedUrls.has(event.url)) {
      this.retriedUrls.add(event.url);
      this.router.navigateByUrl(event.url);
      return;
    }

    this.retriedUrls.delete(event.url);
    this.store.dispatch(
      new OpenModal(ChunkLoadErrorModalComponent, {
        closeByEscape: false,
        closeByBackdropClick: false,
        closeOnNavigation: false
      })
    );
  }
}
